import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { listVendorDirectoryResolutionCases } from "../../api/clientVendorDirectoryResolutionApi";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.resolutionPage";

const STATUS_FILTERS = ["", "ready_for_review", "assigned", "in_review", "stale", "approved", "applied", "deferred", "escalated"];

function statusBadgeClass(status) {
  if (status === "applied") return "cvd-badge--published";
  if (status === "stale" || status === "escalated") return "cvd-badge--suspended";
  if (status === "deferred") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

export default function ClientVendorDirectoryResolutionListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const status = searchParams.get("status") ?? "";
  const page = Number(searchParams.get("page") || 1);
  const basePath = `${serviciosBase}/resolucion`;

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listVendorDirectoryResolutionCases(clientViewId, {
        page,
        pageSize: 25,
        status: status || undefined,
      });
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, page, status]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} />;
  if (errorMessage) return <ClientPortalError message={errorMessage} onRetry={load} />;

  return (
    <div className="cvd-page">
      <header className="cvd-page__header">
        <div>
          <p className="cvd-page__eyebrow">{activeView?.name || "CasaMapa"}</p>
          <h1>{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="cvd-page__subtitle">
            Compare observaciones gobernadas, revise conflictos y apruebe cambios canónicos explícitos.
          </p>
        </div>
      </header>

      <div className="cvd-filter-row">
        <label>
          Estado
          <select
            value={status}
            onChange={(event) => {
              const next = new URLSearchParams(searchParams);
              if (event.target.value) next.set("status", event.target.value);
              else next.delete("status");
              next.delete("page");
              setSearchParams(next);
            }}
          >
            {STATUS_FILTERS.map((value) => (
              <option key={value || "all"} value={value}>
                {value || "Todos"}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="cvd-table-wrap">
        <table className="cvd-table">
          <thead>
            <tr>
              <th>Caso</th>
              <th>Estado</th>
              <th>Asociación</th>
              <th>Materialidad</th>
              <th>Prioridad</th>
              <th>Proveedor</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((item) => (
              <tr key={item.id}>
                <td>
                  <code>{item.id.slice(0, 8)}</code>
                </td>
                <td>
                  <span className={`cvd-badge ${statusBadgeClass(item.status)}`}>{item.status}</span>
                </td>
                <td>{item.associationDisposition}</td>
                <td>{item.highestMateriality || "—"}</td>
                <td>{item.reviewPriority}</td>
                <td>{item.vendorId ? <code>{item.vendorId.slice(0, 8)}</code> : "—"}</td>
                <td>
                  <Link className="cvd-btn cvd-btn--secondary" to={`${basePath}/${item.id}`}>
                    Revisar
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="cvd-page__meta">
        {data?.total || 0} casos · página {data?.page || 1}
      </p>
    </div>
  );
}
